
import { Box, Button, Typography, TextField, Grid } from "@mui/material";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import vehicleMasterService from "../../../services/masters/vehicleMaster.Service";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";

// ── Styles ────────────────────────────────────────────────────────────────────

const pageSx = {
    background: "#F4F7FB",
    minHeight: "100vh",
    padding: "20px 24px 40px",
    fontFamily: "'Inter', Arial, sans-serif",
};

const breadcrumbSx = {
    fontSize: "12px",
    color: "#718096",
    mb: 2,
    display: "flex",
    alignItems: "center",
    gap: "6px",
};

const pageHeaderSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: 2.5,
};

const pageTitleSx = {
    fontSize: "16px",
    fontWeight: 700,
    color: "#000000",
    display: "flex",
    alignItems: "center",
    gap: "10px",
};

const backBtnSx = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#2B6CB0",
    padding: "7px 14px",
    border: "1.5px solid #2B6CB0",
    borderRadius: "7px",
    background: "#fff",
    cursor: "pointer",
    textTransform: "none",
    "&:hover": { background: "#E6F1FB" },
};

const formCardSx = {
    background: "#fff",

    border: "1px solid #E2E8F0",
    overflow: "hidden",
};

const sectionHeaderSx = {
    background: "#1C3A5E",
    padding: "12px 22px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
};

const sectionHeaderTitleSx = {
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    letterSpacing: "0.3px",
};

const formBodySx = { padding: "22px 24px" };

const fieldLabelSx = {
    fontSize: "12px",
    fontWeight: 600,
    color: "#2063b6",
    mb: "4px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
};

const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        fontSize: "13px",
        fontFamily: "'Inter', Arial, sans-serif",
        background: "#fafafa",
        height: "38px",
        "& fieldset": { borderColor: "#E2E8F0", borderWidth: "1.5px" },
        "&:hover fieldset": { borderColor: "#CBD5E0" },
        "&.Mui-focused fieldset": { borderColor: "#2B6CB0", borderWidth: "2px" },
        "&.Mui-focused": { background: "#fff" },
        "&.Mui-disabled": { background: "#F8FAFC" },
    },
    "& input": {
        color: "#1A202C",
        fontSize: "13px",
        fontFamily: "'Inter', Arial, sans-serif",
        "&::placeholder": { color: "#A0AEC0", opacity: 1 },
    },
    "& .MuiInputLabel-root": { display: "none" },
};

const formFooterSx = {
    padding: "18px 24px",
    background: "#F8FAFC",
    borderTop: "1px solid #E2E8F0",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
};

const saveBtnSx = {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    padding: "0 22px",
    height: "40px",
    background: "#4CAF50",
    color: "#fff",
    border: "none",
    fontSize: "14px",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    fontFamily: "'Inter', Arial, sans-serif",
    "&:hover": { background: "#028b19", transform: "translateY(-1px)" },
    "&:active": { transform: "translateY(0)" },
    "&.Mui-disabled": { background: "#94a3b8", color: "#fff" },
    transition: "all 0.15s ease",
};

const cancelBtnSx = {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    padding: "0 22px",
    height: "40px",
    background: "#E53E3E",
    color: "#ffffff",
    border: "none",
    textTransform: "none",
    "&:hover": { background: "#971f16", transform: "translateY(-1px)" },
    fontSize: "14px",
    fontWeight: 600,
    borderRadius: "8px",
};

// ─────────────────────────────────────────────────────────────────────────────

const today = new Date().toISOString().split("T")[0];

const toDateString = (val) => {
    if (!val) return today;
    try { return new Date(val).toISOString().split("T")[0]; }
    catch { return today; }
};

const VehicleAdd = () => {
    const { handleSubmit } = useForm();
    const navigate = useNavigate();
    const urlLocation = useLocation();
    const editData = urlLocation?.state;

    const login_details = sessionStorage.getItem('loginUser');
    const userD = JSON.parse(login_details);
    const userInfo = userD.result.userInfo;

    const [data, setData] = useState({
        vehicle_id: '',
        vehicle_no: '',
        model: '',
        insurance_validity: today,
        road_tax_validity: today,
        puc_validity: today,
        permit_validity: today,
        fuel_efficiency: 0,
        chassis_no: '',
        engine_no: '',
        rc_book_no: '',
        rc_book_validity: today,
        tare_weight: 0,
        max_allowable_weight: 0,
        vehicle_owner: '',
    });

    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Prefill if editing
    useEffect(() => {
        if (editData) {
            setData({
                vehicle_id: editData.vehicle_id || '',
                vehicle_no: editData.vehicle_no || '',
                model: editData.model || '',
                insurance_validity: toDateString(editData.insurance_validity),
                road_tax_validity: toDateString(editData.road_tax_validity),
                puc_validity: toDateString(editData.puc_validity),
                permit_validity: toDateString(editData.permit_validity),
                fuel_efficiency: editData.fuel_efficiency || 0,
                chassis_no: editData.chassis_no || '',
                engine_no: editData.engine_no || '',
                rc_book_no: editData.rc_book_no || '',
                rc_book_validity: toDateString(editData.rc_book_validity),
                tare_weight: editData.tare_weight || 0,
                max_allowable_weight: editData.max_allowable_weight || 0,
                vehicle_owner: editData.vehicle_owner || '',
            });
        }
    }, [editData]);

    const handleChange = ({ target }) => {
        const { name, value } = target;
        setData(prev => ({ ...prev, [name]: value }));
    };

    const onSubmit = async () => {
        setErrorMessage('');

        const vehicleData = {
            ...data,
            modified_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            modified_by: editData ? userInfo.user_id : null,
            created_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            created_by: editData ? editData.created_by : userInfo.user_id,
            changedUpdatedValue: 'edit',
        };

        try {
            if (editData) {
                await vehicleMasterService.editDeleteVehicle(vehicleData);
            } else {
                await vehicleMasterService.addVehicle(vehicleData);
            }
            setSuccessMessage(editData ? "Updated successfully" : "Added successfully");
            setTimeout(() => navigate(-1), 1000);
        } catch (error) {
            console.error("Submission error:", error);
            setErrorMessage("Error occurred while saving data.");
        }
    };

    const handleBack = () => navigate(-1);

    return (
        <Box sx={pageSx}>
            {/* Breadcrumb */}
            <Box sx={breadcrumbSx}>
                <span>Dashboard</span> / <span style={{ color: "#2B6CB0", fontWeight: 600 }}>Vehicle Master</span>
            </Box>

            {/* Page Header */}
            <Box sx={pageHeaderSx}>
                <Box sx={pageTitleSx}>
                    <DirectionsCarIcon sx={{ fontSize: 20, color: "#1C3A5E" }} />
                    {editData ? 'UPDATE' : 'CREATE'} VEHICLE MASTER
                </Box>
                <Button onClick={handleBack} sx={backBtnSx}>
                    <ArrowBackIcon sx={{ fontSize: 16 }} /> Back
                </Button>
            </Box>

            {/* Main Form Card */}
            <Box sx={formCardSx}>
                {/* Navy Strip Header */}
                <Box sx={sectionHeaderSx}>
                    <DirectionsCarIcon sx={{ fontSize: 18, color: "#fff" }} />
                    <Typography sx={sectionHeaderTitleSx}>Vehicle Master</Typography>
                </Box>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={formBodySx}>
                        <Grid container spacing={3}>

                            {/* Row 1 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>
                                        Vehicle No <span style={{ color: "#E53E3E", fontSize: "13px" }}>*</span>
                                    </Typography>
                                    <TextField
                                        size="small" fullWidth type="text"
                                        name="vehicle_no" value={data.vehicle_no}
                                        onChange={handleChange}
                                        placeholder="Enter vehicle number"
                                        required sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>
                                        Vehicle Model <span style={{ color: "#E53E3E", fontSize: "13px" }}>*</span>
                                    </Typography>
                                    <TextField
                                        size="small" fullWidth type="text"
                                        name="model" value={data.model}
                                        onChange={handleChange}
                                        placeholder="Enter vehicle model"
                                        required sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            {/* Row 2 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Insurance Validity</Typography>
                                    <TextField
                                        size="small" fullWidth type="date"
                                        name="insurance_validity" value={data.insurance_validity}
                                        onChange={handleChange} sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Road Tax Validity</Typography>
                                    <TextField
                                        size="small" fullWidth type="date"
                                        name="road_tax_validity" value={data.road_tax_validity}
                                        onChange={handleChange} sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            {/* Row 3 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>PUC Validity</Typography>
                                    <TextField
                                        size="small" fullWidth type="date"
                                        name="puc_validity" value={data.puc_validity}
                                        onChange={handleChange} sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Permit Validity</Typography>
                                    <TextField
                                        size="small" fullWidth type="date"
                                        name="permit_validity" value={data.permit_validity}
                                        onChange={handleChange} sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            {/* Row 4 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Fuel Efficiency (Per Litre)</Typography>
                                    <TextField
                                        size="small" fullWidth type="number"
                                        name="fuel_efficiency" value={data.fuel_efficiency}
                                        onChange={handleChange}
                                        placeholder="Enter fuel efficiency"
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Chassis No</Typography>
                                    <TextField
                                        size="small" fullWidth type="text"
                                        name="chassis_no" value={data.chassis_no}
                                        onChange={handleChange}
                                        placeholder="Enter chassis number"
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            {/* Row 5 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Engine No</Typography>
                                    <TextField
                                        size="small" fullWidth type="text"
                                        name="engine_no" value={data.engine_no}
                                        onChange={handleChange}
                                        placeholder="Enter engine number"
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>RC Book No</Typography>
                                    <TextField
                                        size="small" fullWidth type="text"
                                        name="rc_book_no" value={data.rc_book_no}
                                        onChange={handleChange}
                                        placeholder="Enter RC book number"
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            {/* Row 6 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>RC Book Validity</Typography>
                                    <TextField
                                        size="small" fullWidth type="date"
                                        name="rc_book_validity" value={data.rc_book_validity}
                                        onChange={handleChange} sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>
                                        Tare Weight <span style={{ color: "#E53E3E", fontSize: "13px" }}>*</span>
                                    </Typography>
                                    <TextField
                                        size="small" fullWidth type="number"
                                        name="tare_weight" value={data.tare_weight}
                                        onChange={handleChange}
                                        placeholder="Enter tare weight"
                                        required sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            {/* Row 7 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Maximum Allowable Weight</Typography>
                                    <TextField
                                        size="small" fullWidth type="number"
                                        name="max_allowable_weight" value={data.max_allowable_weight}
                                        onChange={handleChange}
                                        placeholder="Enter maximum allowable weight"
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Vehicle Owner</Typography>
                                    <TextField
                                        size="small" fullWidth type="text"
                                        name="vehicle_owner" value={data.vehicle_owner}
                                        onChange={handleChange}
                                        placeholder="Enter vehicle owner"
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            {/* Success / Error messages */}
                            {successMessage && (
                                <Grid item xs={12}>
                                    <Typography sx={{ color: "#4CAF50", fontSize: "14px", mt: 1 }}>
                                        {successMessage}
                                    </Typography>
                                </Grid>
                            )}
                            {errorMessage && (
                                <Grid item xs={12}>
                                    <Typography sx={{ color: "#E53E3E", fontSize: "14px", mt: 1 }}>
                                        {errorMessage}
                                    </Typography>
                                </Grid>
                            )}

                        </Grid>
                    </Box>

                    {/* Form Footer */}
                    <Box sx={formFooterSx}>
                        <Button sx={cancelBtnSx} onClick={handleBack}>
                            Cancel
                        </Button>
                        <Button type="submit" sx={saveBtnSx}>
                            <SaveIcon sx={{ fontSize: 18 }} />
                            {editData ? "Update" : "Save"}
                        </Button>
                    </Box>
                </form>
            </Box>

            {/* Snackbar */}
            <ReusableSnackbar
                open={!!successMessage}
                onClose={(event, reason) => {
                    if (reason !== 'clickaway') setSuccessMessage('');
                }}
                message={successMessage}
                autoHideDuration={2000}
                vertical="top"
                horizontal="right"
            />
        </Box>
    );
};

export default VehicleAdd;